import React, { useState } from "react";
import { Box, Button, Divider, Grid, Typography } from "@mui/material";

import QTDetailsImageWithLabelSection from "../../../../common/QTDetailsImageWithLabelSection";
import ExternalNarratorTextField from "../../../OrganizationQuickTales/components/ExternalNarratorDataFieldsSection/ExternalNarratorTextField";
import AudioRecordingModalNarrator from "../../../../common/AudioRecordingModalNarrator";
import DOMPurify from "dompurify";
import PlayCircleFilledWhiteOutlinedIcon from "@mui/icons-material/PlayCircleFilledWhiteOutlined";
import AudioModal from "../../../../common/AudioModal";
import { EMAIL_PATTERN } from "../../utils/validation";

/**
 * QuickCollectFormSection
 * The blank guest submission form rendered at
 * /{organizations.handle}/{normalized area.name}/submit
 *
 * Required: Title, Audio, Email, First Name, Last Name.
 * Optional: Photo, Description.
 *
 * See docs/QUICKCOLLECT_IMPLEMENTATION.md
 */
const QuickCollectFormSection = ({
  formValues,
  setFormValues,
  uploadImage,
  setUploadImage,
  uploadAudioFile,
  setUploadAudioFile,
  handleFileUpload,
  onSubmit,
  submitDisabled,
  submitLabel,
  submissionStatus,
  areaName,
  greetingHtml,
  onRecordAnother,
  canEdit,
}) => {
  const [isRecordingModalOpen, setRecordingModalOpen] = useState(false);
  const [isAudioModalOpen, setAudioModalOpen] = useState(false);

  // Only surface the email format error once the guest has left the field, so
  // we aren't flagging a half-typed address as invalid while they type.
  const [emailTouched, setEmailTouched] = useState(false);
  const emailValue = formValues?.email?.trim() || "";
  const emailInvalid =
    emailTouched && emailValue.length > 0 && !EMAIL_PATTERN.test(emailValue);

  // Receives a File directly from the recording modal Upload control.
  const handleUploadChange = (file) => {
    if (!file) return;

    const fileData = { preview: URL.createObjectURL(file), raw: file };
    setUploadAudioFile(fileData);
    setFormValues((prev) => ({ ...prev, originalAudioName: file.name }));
    handleFileUpload?.(fileData);
  };

  const handleRecordingComplete = (audioBlob, durationSeconds) => {
    const mimeType = audioBlob?.type || "audio/webm";

    const extFromMime = (type) => {
      if (!type) return "webm";
      if (type.includes("mp4")) return "mp4";
      if (type.includes("m4a")) return "m4a";
      if (type.includes("aac")) return "aac";
      if (type.includes("ogg")) return "ogg";
      if (type.includes("wav")) return "wav";
      if (type.includes("webm")) return "webm";
      return "webm";
    };

    const title = formValues?.title?.trim();
    const safeBaseName = title
      ? title
      : `Untitled ${new Date().toLocaleString().replace(/[/\\?%*:|"<>]/g, "-")}`;

    const fileName = `${safeBaseName}.${extFromMime(mimeType)}`;
    const file = new File([audioBlob], fileName, { type: mimeType });

    const fileData = {
      preview: URL.createObjectURL(file),
      raw: file,
      durationSeconds:
        typeof durationSeconds === "number" ? durationSeconds : undefined,
    };

    setUploadAudioFile(fileData);
    setFormValues((prev) => ({
      ...prev,
      originalAudioName: file.name,
      ...(typeof durationSeconds === "number"
        ? { audioLength: durationSeconds }
        : {}),
    }));
    handleFileUpload?.(fileData);
  };

  // Prefer the local blob so playback works instantly, before the Storage
  // upload finishes; fall back to the uploaded URL afterwards.
  const audioPreviewSrc = uploadAudioFile?.preview || formValues?.audioUrl || null;

  const setField = (field) => (e) =>
    setFormValues((prev) => ({ ...prev, [field]: e.target.value }));

  if (submissionStatus === "submitting") {
    return (
      <Box sx={{ width: "100%", textAlign: "center", py: 4 }}>
        <Typography sx={submittingStyles}>
          Submission in progress. Please stay on this page until your submission
          is complete...
        </Typography>
      </Box>
    );
  }

  if (submissionStatus === "complete") {
    return (
      <Box sx={{ width: "100%", textAlign: "center", py: 4 }}>
        <Typography sx={{ fontSize: 20, color: "#9447AF" }}>
          Thank you for submitting your story!
        </Typography>
        <Typography sx={{ ...valueSx, fontSize: 16, mt: 3 }}>
          If you need additional help: email{" "}
          <a href="mailto:support@quicktales.com" style={{ color: "#00A7CD" }}>
            support@quicktales.com
          </a>{" "}
          or call{" "}
          <a href="tel:+14242741055" style={{ color: "#00A7CD" }}>
            424-274-1055
          </a>
          .
        </Typography>

        {onRecordAnother ? (
          <Button
            variant="contained"
            onClick={onRecordAnother}
            sx={{ ...recordButtonStyles, width: "auto", px: 3, mt: 3 }}
          >
            RECORD ANOTHER QUICKTALE
          </Button>
        ) : null}
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", textAlign: "left" }}>
      {/* Admin-authored greeting from orgQCTemplates. Tokens are already
          substituted by the container; sanitized here before render. */}
      {greetingHtml ? (
        <Typography
          sx={{
            ...valueSx,
            fontSize: 16,
            mb: 1.5,
          }}
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(greetingHtml, {
              USE_PROFILES: { html: true },
            }),
          }}
        />
      ) : (
        <Box sx={{ textAlign: "center", mb: 2 }}>
          <Typography sx={{ ...valueSx, fontSize: 15 }}>
            Fill out the form below and tap <strong>SUBMIT</strong> to share
            your story.
          </Typography>
        </Box>
      )}

      {/* {formValues?.originalAudioName ? (
        <Typography sx={{ ...valueSx, textAlign: "center", fontSize: 13 }}>
          {formValues.originalAudioName}
        </Typography>
      ) : null}*/}


      {/* <Divider sx={{ mb: 2, mt: 2 }} /> */}
      <Grid container direction="column" sx={{ gap: 1.25 }}>

        <FormRow label="First Name:" required>
          <ExternalNarratorTextField
            value={formValues?.firstName}
            disabled={!canEdit}
            capitalizeWords
            onChange={setField("firstName")}
          />
        </FormRow>

        <FormRow label="Last Name:" required>
          <ExternalNarratorTextField
            value={formValues?.lastName}
            disabled={!canEdit}
            capitalizeWords
            onChange={setField("lastName")}
          />
        </FormRow>

        <FormRow label="Email:" required>
          <ExternalNarratorTextField
            value={formValues?.email}
            disabled={!canEdit}
            type="email"
            placeholder="you@example.com"
            onChange={setField("email")}
            onBlur={() => setEmailTouched(true)}
            error={emailInvalid}
            helperText={
              emailInvalid ? "Please enter a valid email address." : undefined
            }
          />
        </FormRow>

        <FormRow label="Art Title:" required>
          <ExternalNarratorTextField
            value={formValues?.title}
            disabled={!canEdit}
            capitalizeWords
            onChange={setField("title")}
          />
        </FormRow>

        <FormRow label="Description:" alignTop>
          <ExternalNarratorTextField
            value={formValues?.description}
            multiline
            rows={4}
            disabled={!canEdit}
            onChange={setField("description")}
          />
        </FormRow>

    </Grid>
      {/* Photo */}
      <Box sx={{ mb: 2, mt: 2, ml: 5, display: "flex", justifyContent: "center" }}>
        <QTDetailsImageWithLabelSection
          label={""}
          image={uploadImage ? uploadImage.preview : null}
          fileName={""}
          editEnabled={!!canEdit}
          details={formValues}
          setDetails={setFormValues}
          uploadImage={uploadImage}
          setUploadImage={setUploadImage}
          isCreateMode={true}
          autoOpenCropOnUpload={true}
          disablePreview={true}
        />
      </Box>

        {/* Audio: label is always shown; the play button appears once the
            guest has recorded or uploaded. Mirrors the Recording row on the
            AddToQuickTale page. */}
        <Grid item sx={{ display: "flex", alignItems: "center", mt: "-16px", mb: 2 }}>
          <Typography component="span" sx={labelSx}>
            Audio:
          </Typography>
          {audioPreviewSrc ? (
            <PlayCircleFilledWhiteOutlinedIcon
              fontSize="large"
              onClick={() => setAudioModalOpen(true)}
              sx={{ color: "#00A7CD", cursor: "pointer", mr: 1 }}
            />
          ) : null}
        </Grid>
      <Box
        sx={{
          display: "flex",
          gap: 2,
          justifyContent: "center",
          alignItems: "stretch",
          mb: 1.25,
        }}
      >
        <Button
          variant="contained"
          onClick={() => setRecordingModalOpen(true)}
          disabled={!canEdit}
          sx={recordButtonStyles}
        >
          RECORD AUDIO
        </Button>

        <Button
          variant="contained"
          onClick={onSubmit}
          disabled={!!submitDisabled}
          sx={submitButtonStyles}
        >
          {submitLabel || "SUBMIT"}
        </Button>
      </Box>
      <AudioRecordingModalNarrator
        open={isRecordingModalOpen}
        handleClose={() => setRecordingModalOpen(false)}
        onRecordingComplete={handleRecordingComplete}
        onFileSelected={handleUploadChange}
      />

      <AudioModal
        modalOpen={isAudioModalOpen}
        handleClose={() => setAudioModalOpen(false)}
        audioFile={audioPreviewSrc}
      />
    </Box>
  );
};

const FormRow = ({ label, required, alignTop, children }) => (
  <Grid
    item
    sx={{ display: "flex", alignItems: alignTop ? "flex-start" : "baseline" }}
  >
    <Typography component="span" sx={{ ...labelSx, mt: alignTop ? "2px" : 0 }}>
      {label}
      {required ? <span style={{ color: "#BA344C" }}> *</span> : null}
    </Typography>
    <Box sx={valueCellSx}>{children}</Box>
  </Grid>
);

export default QuickCollectFormSection;

const labelSx = {
  width: 110,
  minWidth: 110,
  fontSize: 14,
  fontWeight: 600,
  color: "#020303",
  fontFamily: "Avenir Book, sans-serif",
};

const valueSx = {
  fontSize: 14,
  fontWeight: 400,
  color: "#020303",
  fontFamily: "Avenir Book, sans-serif",
  whiteSpace: "pre-line",
};

const valueCellSx = { flex: 1, maxWidth: 420 };

const submittingStyles = {
  fontSize: 20,
  color: "#fff",
  backgroundColor: "#7f3c97",
  padding: 2,
  borderRadius: 10,
};

const recordButtonStyles = {
  bgcolor: "#00A7CD",
  color: "#fff",
  fontWeight: 700,
  px: 3,
  borderRadius: 1,
  width: "160px",
  "&:hover": { bgcolor: "#008bb0" },
};

const submitButtonStyles = {
  bgcolor: "#9447AF",
  color: "#fff",
  fontWeight: 700,
  px: 3,
  borderRadius: 1,
  width: "160px",
  "&:hover": { bgcolor: "#7f3c97" },
  // Stay grey until every required field is filled in. MUI's default disabled
  // styling would otherwise win over `bgcolor`, so set it explicitly.
  "&.Mui-disabled": {
    bgcolor: "#BDBDBD",
    color: "#FFFFFF",
  },
};
